import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import type { CredentialResponse } from '@react-oauth/google'
import { AlertCircle, Eye, EyeOff } from 'lucide-react'
import { clientConfig } from '@/config'
import { usePricingPlans } from '@/hooks/usePricingPlans'
import { SEO } from '@/components/SEO/SEO'
import { AuthExperience } from '@/components/Public/AuthExperience'
import { getSignupMarketing } from './authMarketing'
import { useAuth } from '@/contexts/AuthContext'
import { API_BASE } from '@/services/api'

const GOOGLE_CLIENT_ID = clientConfig.googleClientId
const GOOGLE_SSO_ENABLED = Boolean(GOOGLE_CLIENT_ID && clientConfig.features.enableOAuth)

type SignupResponse = {
  success: boolean
  message?: string
  data?: {
    accessToken?: string
    access_token?: string
    refreshToken?: string
    user?: {
      id: string
      email: string
      firstName?: string | null
      lastName?: string | null
      tenant_id?: string
      role?: string
      is_active?: boolean
    }
  }
}

const parseResponseDetail = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') return ''
  const maybeDetail = (payload as Record<string, unknown>).detail
  if (typeof maybeDetail === 'string') return maybeDetail
  const maybeMessage = (payload as Record<string, unknown>).message
  return typeof maybeMessage === 'string' ? maybeMessage : ''
}

const safeSignupError = (status: number, detail: string): string => {
  const normalized = detail.toLowerCase()
  if (status === 429) return 'Too many signup attempts. Please try again in a few minutes.'
  if (status === 400 && normalized.includes('password')) return detail
  if (status === 400 && normalized.includes('invite')) return 'We could not validate that invite. Please check the code and try again.'
  if (status === 400 && normalized.includes('email')) return 'Please enter a valid email address.'
  if (status === 400) return 'Please review your details and try again.'
  if (status === 503) return "We couldn't send the verification email. Please try again in a moment."
  if (status >= 500) return "We couldn't complete registration right now. Please try again."
  return "We couldn't complete registration. Please try again."
}

const safeGoogleSignupError = (status: number, detail: string): string => {
  if (status === 429) return 'Too many signup attempts. Please try again in a few minutes.'
  if (status === 400 && detail.toLowerCase().includes('verified')) return 'Your Google email must be verified before continuing.'
  if (status >= 500) return 'Google sign-up is temporarily unavailable. Please try again.'
  return 'Google sign-up failed. Please try again.'
}

const SignupPageContent: React.FC = () => {
  const navigate = useNavigate()
  const { establishSession } = useAuth()
  const [searchParams] = useSearchParams()
  const { marketingCopy } = usePricingPlans()
  const marketing = getSignupMarketing(marketingCopy)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [inviteCode, setInviteCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const codeFromUrl = searchParams.get('invite') || searchParams.get('invite_code')
    if (codeFromUrl) setInviteCode(codeFromUrl.trim())
  }, [searchParams])

  const isInvited = Boolean(inviteCode)
  const canSubmit = Boolean(email.trim() && password && confirmPassword)
  const formBadge = isInvited ? 'Invite ready' : marketing.formBadge
  const formSubheadline = isInvited
    ? "You've been invited to create a Ricald workspace."
    : marketing.formSubheadline

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!canSubmit) {
      setError('Please enter your email and password')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)
    try {
      const body: Record<string, string> = { email: email.trim().toLowerCase(), password, intent: 'create' }
      if (inviteCode) body.invite_code = inviteCode.trim()
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(safeSignupError(response.status, parseResponseDetail(payload)))
      }

      const data = (await response.json()) as SignupResponse
      const user = data.data?.user
      const token = data.data?.accessToken || data.data?.access_token
      const verificationEmail = user?.email || email.trim().toLowerCase()

      if (token && user?.id && user.email) {
        establishSession(data as unknown as Record<string, unknown>)
        navigate('/app', { replace: true })
        return
      }

      try {
        localStorage.setItem('pending_verification_email', verificationEmail)
      } catch {
        // Verification continues using route state when storage is unavailable.
      }
      navigate('/verify-email', {
        replace: true,
        state: { email: verificationEmail, returnTo: '/app' },
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignupSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError('No credential received from Google. Please try again.')
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const signupResponse = await fetch(`${API_BASE}/auth/google-signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: response.credential }),
      })
      if (!signupResponse.ok) {
        const payload = await signupResponse.json().catch(() => ({}))
        throw new Error(safeGoogleSignupError(signupResponse.status, parseResponseDetail(payload)))
      }
      const data = (await signupResponse.json()) as SignupResponse
      const user = data.data?.user
      const token = data.data?.accessToken || data.data?.access_token
      if (!token || !user?.id || !user.email) throw new Error("We couldn't complete Google sign-up. Please try again.")

      establishSession(data as unknown as Record<string, unknown>)
      navigate('/app', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Google sign-up failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="pp-page">
      <SEO
        title="Create your Ricald account"
        description="Create a Ricald account and coordinate commercial work around one persistent outcome."
        noIndex
      />
      <AuthExperience marketing={marketing}>
        <div className="pp-auth-card">
          <header className="pp-auth-card__head">
            <p className="pp-auth-card__badge">{formBadge}</p>
            <h2>{marketing.formTitle}</h2>
            <p>{formSubheadline}</p>
          </header>

          {error && <div className="pp-auth-error" role="alert"><AlertCircle aria-hidden="true" /><span>{error}</span></div>}

          <form onSubmit={handleSubmit}>
            <div className="pp-field">
              <label htmlFor="signup-email">Email address</label>
              <input id="signup-email" type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com" required />
            </div>
            <div className="pp-field">
              <label htmlFor="signup-password">Password</label>
              <div className="pp-password">
                <input id="signup-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="8+ characters" minLength={8} required />
                <button type="button" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? 'Hide password' : 'Show password'}>{showPassword ? <EyeOff /> : <Eye />}</button>
              </div>
            </div>
            <div className="pp-field">
              <label htmlFor="signup-confirm-password">Confirm password</label>
              <div className="pp-password">
                <input id="signup-confirm-password" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} placeholder="Re-enter your password" minLength={8} required />
                <button type="button" onClick={() => setShowConfirmPassword((visible) => !visible)} aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>{showConfirmPassword ? <EyeOff /> : <Eye />}</button>
              </div>
            </div>
            {isInvited && (
              <div className="pp-field">
                <label htmlFor="signup-invite">Invite code</label>
                <input id="signup-invite" type="text" readOnly value={inviteCode} />
              </div>
            )}
            <button className="pp-auth-submit" type="submit" disabled={isLoading || !canSubmit}>
              {isLoading ? 'Creating workspace…' : marketing.submitLabel}
            </button>
          </form>

          {GOOGLE_SSO_ENABLED && (
            <>
              <div className="pp-auth-divider">or continue with</div>
              <div className="pp-auth-google">
                <GoogleLogin onSuccess={handleGoogleSignupSuccess} onError={() => setError('Google sign-up failed. Please try again.')} theme="outline" size="large" text="signup_with" shape="rectangular" width="300" locale="en" />
              </div>
            </>
          )}

          <p className="pp-auth-terms">By creating an account, you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
        </div>
        <p className="pp-auth-switch">{marketing.secondaryPrompt} <Link to="/login">{marketing.secondaryActionLabel}</Link></p>
        <p className="pp-auth-note">{marketing.footerNote}</p>
      </AuthExperience>
    </div>
  )
}

export const SignupPage: React.FC = () => {
  if (GOOGLE_SSO_ENABLED && GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><SignupPageContent /></GoogleOAuthProvider>
  }
  return <SignupPageContent />
}

export default SignupPage
